import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface MarginSettings {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface MarginContextType {
  margins: MarginSettings;
  setMargins: (margins: MarginSettings) => void;
  updateMargin: (key: keyof MarginSettings, value: number) => void;
  resetToDefaults: () => void;
}

const STORAGE_KEY = 'resume-editor-margins';

const DEFAULT_MARGINS: MarginSettings = {
  top: 0.2,
  bottom: 0.2,
  left: 0.2,
  right: 0.2,
};

const MarginContext = createContext<MarginContextType | undefined>(undefined);

function loadMarginsFromStorage(): MarginSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const loadedMargins = {
        top: typeof parsed.top === 'number' ? parsed.top : DEFAULT_MARGINS.top,
        bottom: typeof parsed.bottom === 'number' ? parsed.bottom : DEFAULT_MARGINS.bottom,
        left: typeof parsed.left === 'number' ? parsed.left : DEFAULT_MARGINS.left,
        right: typeof parsed.right === 'number' ? parsed.right : DEFAULT_MARGINS.right,
      };
      
      // Migrate from old defaults (0.5/0.75) to new defaults (0.2)
      const isOldDefaults = 
        (loadedMargins.top === 0.5 && loadedMargins.bottom === 0.5 && 
         loadedMargins.left === 0.75 && loadedMargins.right === 0.75);
      
      if (isOldDefaults) {
        // Clear old defaults and use new ones
        localStorage.removeItem(STORAGE_KEY);
        return DEFAULT_MARGINS;
      }
      
      return loadedMargins;
    }
  } catch (e) {
    console.error('Failed to load margin settings:', e);
  }
  return DEFAULT_MARGINS;
}

function saveMarginsToStorage(margins: MarginSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(margins));
  } catch (e) {
    console.error('Failed to save margin settings:', e);
  }
}

interface MarginProviderProps {
  children: ReactNode;
}

export const MarginProvider: React.FC<MarginProviderProps> = ({ children }) => {
  const [margins, setMarginsState] = useState<MarginSettings>(loadMarginsFromStorage);

  useEffect(() => {
    saveMarginsToStorage(margins);
  }, [margins]);

  const setMargins = useCallback((newMargins: MarginSettings) => {
    setMarginsState(newMargins);
  }, []);

  const updateMargin = useCallback((key: keyof MarginSettings, value: number) => {
    setMarginsState(prev => ({
      ...prev,
      [key]: Math.max(0.1, Math.min(1.5, value)),
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setMarginsState(DEFAULT_MARGINS);
  }, []);

  return (
    <MarginContext.Provider value={{ margins, setMargins, updateMargin, resetToDefaults }}>
      {children}
    </MarginContext.Provider>
  );
};

export function useMargins(): MarginContextType {
  const context = useContext(MarginContext);
  if (!context) {
    throw new Error('useMargins must be used within a MarginProvider');
  }
  return context;
}

export { DEFAULT_MARGINS };
